import { createRouter, createWebHistory } from 'vue-router'
import MainLayout from '@/layouts/MainLayout.vue'
import BlankLayout from '@/layouts/BlankLayout.vue'
import { useUserStore } from '@/stores/user'
import { useMenuStore } from '@/stores/menu'

// 创建带有错误处理的懒加载组件函数
function lazyLoad(componentImport) {
  return () =>
    componentImport().catch((err) => {
      console.error('Component load failed:', err)
      // 返回一个错误提示组件
      return {
        template: `
          <div style="padding: 40px; text-align: center;">
            <h2>页面加载失败</h2>
            <p>请刷新页面重试</p>
            <button
              onclick="window.location.reload()"
              style="margin-top: 20px; padding: 10px 20px; cursor: pointer;"
            >
              刷新页面
            </button>
          </div>
        `,
      }
    })
}

// 根据用户角色获取默认首页
function getDefaultHomePath(userRole) {
  // 角色 1, 2, 6: 律师事务所列表
  if ([1, 2, 6].includes(userRole)) {
    return '/base/office-list'
  }
  // 角色 3, 4, 5: 案件列表
  if ([3, 4, 5].includes(userRole)) {
    return '/case/list'
  }
  // 其他角色: 案件列表（最通用的页面）
  return '/case/list'
}

const routes = [
  // 处理直接访问 /index.html 的情况，重定向到根路径
  {
    path: '/index.html',
    redirect: '/',
  },
  {
    path: '/login',
    component: BlankLayout,
    children: [
      {
        path: '',
        name: 'Login',
        component: lazyLoad(() => import('@/views/login/LoginView.vue')),
        meta: { title: '登录', public: true },
      },
    ],
  },
  {
    path: '/',
    component: MainLayout,
    // 不使用固定重定向,在路由守卫中动态处理
    redirect: (to) => {
      const loginData = localStorage.getItem('login_data')
      if (loginData) {
        try {
          const userData = JSON.parse(loginData)
          return getDefaultHomePath(userData.role)
        } catch {
          return '/case/list'
        }
      }
      return '/login'
    },
    children: [
      // ============ 基础信息模块 ============
      {
        path: 'base/tag-list',
        name: 'TagList',
        component: lazyLoad(() => import('@/views/base/TagList.vue')),
        meta: { title: '标签管理', roleList: [1] },
      },
      {
        path: 'base/office-list',
        name: 'OfficeList',
        component: lazyLoad(() => import('@/views/base/OfficeList.vue')),
        meta: { title: '律师事务所', roleList: [1, 2, 6] },
      },
      {
        path: 'base/office-detail/:id?',
        name: 'OfficeDetail',
        component: lazyLoad(() => import('@/views/base/OfficeDetail.vue')),
        meta: { title: '事务所详情', roleList: [1, 2, 6] },
      },
      {
        path: 'base/user-list',
        name: 'UserList',
        component: lazyLoad(() => import('@/views/base/UserList.vue')),
        meta: { title: '人员信息', roleList: [1, 2, 5, 6] },
      },
      {
        path: 'base/consultant-unit-list',
        name: 'ConsultantUnitList',
        component: lazyLoad(() => import('@/views/base/ConsultantUnitList.vue')),
        meta: { title: '顾问单位', roleList: [1, 2, 3, 5] },
      },
      {
        path: 'base/trust-office-list',
        name: 'TrustOfficeList',
        component: lazyLoad(() => import('@/views/base/TrustOfficeList.vue')),
        meta: { title: '委托单位', roleList: [1, 2, 3] },
      },
      {
        path: 'base/trust-user-list',
        name: 'TrustUserList',
        component: lazyLoad(() => import('@/views/base/TrustUserList.vue')),
        meta: { title: '委托人员', roleList: [1, 2, 3] },
      },
      {
        path: 'base/lawyer-list',
        name: 'LawyerList',
        component: lazyLoad(() => import('@/views/base/LawyerList.vue')),
        meta: { title: '律师通讯录', roleList: [1, 2, 3, 4, 5, 6] },
      },
      {
        path: 'base/notice-list',
        name: 'NoticeList',
        component: lazyLoad(() => import('@/views/base/NoticeList.vue')),
        meta: { title: '通知', roleList: [1, 2, 3, 4, 5, 6] },
      },
      {
        path: 'base/notice-add/:id?',
        name: 'NoticeAdd',
        component: lazyLoad(() => import('@/views/base/NoticeAdd.vue')),
        meta: { title: '通知编辑', roleList: [1, 2, 3, 4, 5, 6] },
      },
      {
        path: 'base/update-log',
        name: 'UpdateLog',
        component: lazyLoad(() => import('@/views/base/UpdateLog.vue')),
        meta: { title: '更新日志', roleList: [1, 2, 3, 4, 5, 6] },
      },
      {
        path: 'base/template-list',
        name: 'TemplateList',
        component: lazyLoad(() => import('@/views/base/TemplateList.vue')),
        meta: { title: '模板管理', roleList: [1] },
      },
      {
        path: 'base/template-add/:id?',
        name: 'TemplateAdd',
        component: lazyLoad(() => import('@/views/base/TemplateAdd.vue')),
        meta: { title: '模板编辑', roleList: [1] },
      },
      {
        path: 'base/doc-display-rule',
        name: 'DocDisplayRule',
        component: lazyLoad(() => import('@/views/base/DocDisplayRule.vue')),
        meta: { title: '文书展示', roleList: [1] },
      },

      // ============ 互动交流模块 ============
      {
        path: 'circle/lawyer-circle',
        name: 'LawyerCircle',
        component: lazyLoad(() => import('@/views/circle/LawyerCircle.vue')),
        meta: { title: '律师圈子', roleList: [1, 2, 3, 4, 5, 6] },
      },
      {
        path: 'circle/lawyer-space',
        name: 'LawyerSpace',
        component: lazyLoad(() => import('@/views/circle/LawyerSpace.vue')),
        meta: { title: '律师专属空间', roleList: [1, 2, 3, 4, 5, 6] },
      },

      // ============ 案件处理模块 ============
      {
        path: 'case/list',
        name: 'CaseList',
        component: lazyLoad(() => import('@/views/case/CaseList.vue')),
        meta: { title: '案件列表' },
      },
      {
        path: 'case/detail/:id?',
        name: 'CaseDetail',
        component: lazyLoad(() => import('@/views/case/CaseList.vue')),
        meta: { title: '案件详情' },
      },
      {
        path: 'case/adviser-office-list',
        name: 'AdviserOfficeList',
        component: lazyLoad(() => import('@/views/case/AdviserOfficeList.vue')),
        meta: { title: '顾问业务' },
      },
      {
        path: 'case/conflict-check',
        name: 'ConflictCheck',
        component: lazyLoad(() => import('@/views/case/ConflictCheck.vue')),
        meta: { title: '利益冲突自查' },
      },

      // ============ 财务管理模块 ============
      {
        path: 'finance/case-payment',
        name: 'CasePayment',
        component: lazyLoad(() => import('@/views/finance/CasePayment.vue')),
        meta: { title: '案件收款' },
      },

      // ============ 辅助功能 ============
      {
        path: 'change-password',
        name: 'ChangePassword',
        component: lazyLoad(() => import('@/views/common/ChangePassword.vue')),
        meta: { title: '修改密码' },
      },

      // ============ 数据看板模块 ============
      {
        path: 'bi/site-data',
        name: 'SiteData',
        component: lazyLoad(() => import('@/views/bi/SiteData.vue')),
        meta: { title: '站点数据', roleList: [2] },
      },
      {
        path: 'bi/system-data',
        name: 'SystemData',
        component: lazyLoad(() => import('@/views/bi/SystemData.vue')),
        meta: { title: '系统数据', roleList: [1, 6] },
      },
      {
        path: 'bi/advanced-dashboard',
        name: 'AdvancedDashboard',
        component: lazyLoad(() => import('@/views/bi/AdvancedDashboard.vue')),
        meta: { title: '高级看板', roleList: [1, 6] },
      },
      {
        path: 'bi/print-doc-stats',
        name: 'PrintDocStats',
        component: lazyLoad(() => import('@/views/bi/PrintDocStats.vue')),
        meta: { title: '文书统计', roleList: [1, 6] },
      },
      {
        path: 'bi/personnel-stats',
        name: 'PersonnelStats',
        component: lazyLoad(() => import('@/views/bi/PersonnelStats.vue')),
        meta: { title: '人员统计', roleList: [1, 6] },
      },
      {
        path: 'bi/operation-log',
        name: 'OperationLog',
        component: lazyLoad(() => import('@/views/bi/OperationLog.vue')),
        meta: { title: '操作日志', roleList: [1] },
      },
      {
        path: 'bi/api-stats',
        name: 'ApiStats',
        component: lazyLoad(() => import('@/views/bi/ApiStats.vue')),
        meta: { title: '接口统计', roleList: [1] },
      },
      {
        path: 'system/log-management',
        name: 'LogManagement',
        component: lazyLoad(() => import('@/views/system/LogManagement.vue')),
        meta: { title: '日志管理', roleList: [1] },
      },
      {
        path: 'search/engine',
        name: 'SearchEngine',
        component: lazyLoad(() => import('@/views/search/SearchEngine.vue')),
        meta: { title: '搜索引擎', roleList: [1] },
      },

      // ============ 电子签章模块 ============
      {
        path: 'esign/management',
        name: 'EsignManagement',
        component: lazyLoad(() => import('@/views/esign/EsignManagement.vue')),
        meta: { title: '电子签章', roleList: [1, 2, 3, 4, 5, 6] },
      },

      // ============ 系统设置模块 ============
      {
        path: 'system/role-perm',
        name: 'RolePerm',
        component: lazyLoad(() => import('@/views/system/RolePerm.vue')),
        meta: { title: '角色权限', roleList: [1] },
      },
      {
        path: 'system/risk-control',
        name: 'RiskControl',
        component: lazyLoad(() => import('@/views/system/RiskControl.vue')),
        meta: { title: '风控检测', roleList: [1] },
      },
    ],
  },
]

const router = createRouter({
  history: createWebHistory('/'),
  routes,
})

// 全局前置守卫
router.beforeEach(async (to, from, next) => {
  try {
    console.log('[LSV] guard:', to.path, 'from:', from.path)
    // 设置页面标题
    if (to.meta.title) {
      document.title = `常熟律管系统V5 - ${to.meta.title}`
    }

    // 公开页面直接放行
    if (to.meta.public) {
      // 已登录用户访问登录页,重定向到首页
      const loginData = localStorage.getItem('login_data')
      if (loginData && to.path === '/login') {
        next('/')
        return
      }
      next()
      return
    }

    // 检查登录状态 - 直接读取 localStorage
    const loginData = localStorage.getItem('login_data')

    // 未登录或登录数据无效,跳转到登录页
    if (!loginData) {
      next('/login')
      return
    }

    // 验证登录数据格式
    try {
      const userData = JSON.parse(loginData)
      if (!userData || !userData.token) {
        // 登录数据无效,清除并跳转到登录页
        localStorage.removeItem('login_data')
        next('/login')
        return
      }
    } catch (e) {
      // JSON 解析失败,清除并跳转到登录页
      localStorage.removeItem('login_data')
      next('/login')
      return
    }

    // 强制修改密码拦截：若密码已过期，只允许访问修改密码页
    const userStore = useUserStore()
    if (userStore.isLoggedIn && !userStore.pwdSecurity.checked) {
      await userStore.checkPwdSecurity()
    }
    if (userStore.pwdSecurity.forced && to.path !== '/change-password') {
      next('/change-password')
      return
    }

    // 菜单权限（数据表驱动）：后台静默加载 DB 菜单，不阻塞导航（接口异常/超时时页面降级内置菜单）；
    // 已加载完成时，受管控路径必须在用户可见菜单内，否则拦截；未加载完成时降级为 meta.roleList 校验
    const menuStore = useMenuStore()
    menuStore.loadMenusFromDb()
    console.log('[LSV] guard menuFromDb=', menuStore.menuFromDb, 'path controlled=', menuStore.allMenuPaths.includes(to.path))
    if (menuStore.menuFromDb && menuStore.allMenuPaths.includes(to.path)) {
      const allowed = menuStore.allowedPaths()
      if (!allowed.includes(to.path)) {
        console.warn('[LSV] guard blocked by db menu:', to.path)
        // 找到用户有权限的第一个页面跳转，避免 next('/') 造成循环
        const fallback = allowed.find((p) => p && p !== to.path) || '/login'
        next(fallback)
        return
      }
    }

    // 角色权限校验
    if (to.meta.roleList && to.meta.roleList.length > 0) {
      try {
        const userData = JSON.parse(loginData)
        if (!to.meta.roleList.includes(userData.role)) {
          // 无权限,检测是否是首页重定向
          if (to.path === '/' || to.path === '/base/office-list') {
            // 如果是首页重定向且无权限,根据角色跳转到合适的首页
            const homePath = getDefaultHomePath(userData.role)
            next(homePath)
          } else {
            // 其他页面无权限,跳转到首页
            next('/')
          }
          return
        }
      } catch {
        next('/login')
        return
      }
    }

    next()
    console.log('[LSV] guard next():', to.path)
  } catch (err) {
    // 路由守卫发生任何错误，清除数据并跳转到登录页
    console.error('Router guard error:', err)
    try {
      localStorage.removeItem('login_data')
    } catch (e) {
      // ignore
    }
    next('/login')
  }
})

// 全局后置守卫
router.afterEach((to, from) => {
  // 确保页面滚动到顶部
  window.scrollTo(0, 0)
})

// 路由错误处理
router.onError((error) => {
  console.error('Router error:', error)

  // 隐藏加载动画
  const loading = document.getElementById('app-loading')
  if (loading) {
    loading.style.display = 'none'
  }

  // 检查是否是组件加载失败
  const errorMsg = error.message || error.toString()
  const isLoadError =
    errorMsg.includes('Failed to fetch') ||
    errorMsg.includes('Loading chunk') ||
    errorMsg.includes('error loading dynamically imported module') ||
    errorMsg.includes('Cannot find module') ||
    errorMsg.includes('NetworkError') ||
    errorMsg.includes('timeout') ||
    errorMsg.includes('ChunkLoadError')

  if (isLoadError) {
    console.log('Component load error detected, will redirect to login...')

    // 尝试重新加载页面
    if (!window.__retry_count) {
      window.__retry_count = 0
    }

    if (window.__retry_count < 2) {
      window.__retry_count++
      console.log(`Retrying... attempt ${window.__retry_count}`)
      // 延迟重试
      setTimeout(() => {
        window.location.reload()
      }, 1000)
    } else {
      // 重试次数用尽，清除数据并跳转到登录页
      window.__retry_count = 0
      console.log('Max retries reached, redirecting to login...')
      try {
        localStorage.removeItem('login_data')
      } catch (e) {
        // ignore
      }
      window.location.href = '/login'
    }
  } else {
    // 其他错误也跳转到登录页
    console.log('Unknown router error, redirecting to login...')
    try {
      localStorage.removeItem('login_data')
    } catch (e) {
      // ignore
    }
    window.location.href = '/login'
  }
})

export default router
